import React, { FC, useEffect } from 'react'
import Head from 'next/head'
import { useDispatch, useSelector } from 'react-redux'
import { Device } from 'react-components'

import { HomePage } from '@src/components'
import BaseTransport from '@common/transports/common/base-transport'
import DeprecatedHomePageTransport from '@common/transports/deprecated-home-page-transport'
import { getCheckDeviceSessionTransport, getIsUserLoggedIn } from '@src/redux/user'
import HomePageStyles from '@src/components/home-page/styles'
import { PageProps } from '@src/types'
import { Language, RequestUserConfig } from '@common/types'
import { GetServerSideProps } from '@src/redux/types'
import { pageClient } from '@common/clients'
import { useTrackUserEntrance } from '@src/hooks/tracking'
import { EventBeaconEvent } from '@common/transports/event-beacon-transport'
import Page from '@src/components/common/page'
import { getMessagesFromLanguage } from '@src/i18n/utils'

type Props = {
    baseHomePageTransport: BaseTransport<DeprecatedHomePageTransport>
} & PageProps

const HomePageContainer: FC<Props> = ({ baseHomePageTransport }) => {
    const isLoggedIn = useSelector(getIsUserLoggedIn)
    const dispatch = useDispatch()

    // On load, check if device session is valid.
    useEffect(() => {
        dispatch(getCheckDeviceSessionTransport())
    }, [dispatch])

    useTrackUserEntrance(EventBeaconEvent.LANDING_PAGE_INDEX)

    const { data: homePageTransport } = baseHomePageTransport

    const {
        headerSection,
        heroSection,
        customerSupportSection,
        benefitsSection,
        testimonialsSection,
        howItWorksSection,
        moneyBackGuaranteeSection,
        faqSection,
        customerSupportFooterSection,
        footerSection,
        head,
    } = homePageTransport

    return (
        <Page head={head}>
            <Head>
                <link
                    rel="stylesheet"
                    type="text/css"
                    charSet="UTF-8"
                    href="https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick.min.css"
                />
                <link
                    rel="stylesheet"
                    type="text/css"
                    href="https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick-theme.min.css"
                />
            </Head>
            <HomePageStyles />

            <HomePage
                headerSection={headerSection}
                heroSection={heroSection}
                customerSupportSection={customerSupportSection}
                benefitsSection={benefitsSection}
                testimonialsSection={testimonialsSection}
                howItWorksSection={howItWorksSection}
                moneyBackGuaranteeSection={moneyBackGuaranteeSection}
                faqSection={faqSection}
                customerSupportFooterSection={customerSupportFooterSection}
                footerSection={footerSection}
                isLoggedIn={isLoggedIn}
            />
        </Page>
    )
}

const _germanLanguages = new Set(['de_DE', 'de_CH', 'de_AT'])

export const getServerSideProps: GetServerSideProps<Props> = async ({ res }) => {
    const { user, device }: { user: RequestUserConfig; device: Device } = res.locals

    // For this page, the german languages correspond to `de` only.
    if (_germanLanguages.has(user.language)) {
        user.language = 'de'
    }

    const data = await pageClient.getDeprecatedHomePage({
        headers: { 'x-language': user.language, 'x-domain': user.domain },
    })

    return {
        props: {
            baseHomePageTransport: data,
            device,
            requestUserConfig: user,
            messages: await getMessagesFromLanguage(user.language as Language),
        },
    }
}

export default HomePageContainer
